// Company → Modules: owner or admin turns modules on, starts trials, or locks them.
// Writes <settings>/modules; every signed-in phone picks the change up live.
import { useState } from 'react';
import { Link } from 'react-router';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { listModules, moduleState, trialDaysLeft, missingDependencies, startTrial, setModule, ENTITLEMENTS_DOC, type Entitlements } from '@tradeview/core';
import { canSeeAdminPanel } from '@tradeview/shared';
import { db } from '../firebase';
import { useAuth } from '../auth';
import { Icon } from '../app/Icon';
import { toast } from '../app/toast';
import { price } from '../lib/price';

export function ManageModules() {
  const { session } = useAuth();
  const [busy, setBusy] = useState<string | null>(null);
  if (!session) return null;
  if (!canSeeAdminPanel(session.role)) {
    return <div className="card mt-2"><b className="font-head font-bold block">Owner or office admin only</b><span className="text-muted text-[13.5px]">Ask the office to change which modules are on.</span></div>;
  }
  const ent = session.entitlements;
  async function save(next: Entitlements, msg: string) {
    setBusy(msg);
    try {
      await setDoc(doc(db, `${session!.paths.settings}/${ENTITLEMENTS_DOC}`), { ...next, updatedAt: serverTimestamp(), updatedBy: session!.user.uid }, { merge: false });
      toast(msg);
    } catch (e) { toast(`Couldn't save: ${e instanceof Error ? e.message : e}`, 'crit'); }
    finally { setBusy(null); }
  }
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center gap-2.5 pt-1.5">
        <Link to="/settings" className="w-9 h-9 rounded-full bg-surface border border-line grid place-items-center" aria-label="Back"><Icon name="back" size={18} /></Link>
        <h2 className="font-disp font-bold text-[26px] leading-none m-0 flex-1">Manage modules</h2>
      </div>
      <p className="m-0 text-muted text-[13.5px]">Changes apply to every phone the moment you save. Billing arrives in Phase 6; until then this switch is the plan.</p>
      {listModules().map(m => {
        const id = m.manifest.id; const st = moduleState(ent, id); const days = trialDaysLeft(ent, id); const missing = missingDependencies(id, ent);
        const core = m.manifest.bundle === 'core';
        return (
          <div key={id} className="card">
            <div className="flex items-center gap-3">
              <div className={`ic ${st === 'active' ? 'ic-ok' : st === 'trial' ? 'ic-warn' : 'ic-lock'}`}><Icon name={st === 'locked' ? 'lock' : m.manifest.nav[0]?.icon ?? 'grid'} /></div>
              <div className="min-w-0 flex-1"><b className="block font-semibold">{m.manifest.name}</b><small className="block text-muted text-[12.5px]">{price(m.manifest.sku)} · {m.manifest.bundle}{missing.length ? ` · needs ${missing.join(', ')}` : ''}</small></div>
              <span className={`pill pill-nodot ${st === 'active' ? 'pill-ok' : st === 'trial' ? 'pill-warn' : 'pill-lock'}`}>{st === 'active' ? 'On' : st === 'trial' ? `Trial · ${days}d` : 'Off'}</span>
            </div>
            {!core && (
              <div className="flex gap-2 mt-2.5">
                {st !== 'active' && <button className="btn btn-sm btn-primary" disabled={!!busy || missing.length > 0} onClick={() => save(setModule(ent, id, 'active'), `${m.manifest.name} is on`)}>Turn on</button>}
                {st === 'locked' && <button className="btn btn-sm btn-ghost" disabled={!!busy || missing.length > 0} onClick={() => save(startTrial(ent, id, m.manifest.sku.trialDays), `${m.manifest.name} trial started`)}>Start {m.manifest.sku.trialDays}-day trial</button>}
                {st !== 'locked' && <button className="btn btn-sm" disabled={!!busy} onClick={() => save(setModule(ent, id, 'locked'), `${m.manifest.name} is off`)}>Turn off</button>}
              </div>
            )}
            {core && <small className="block text-muted text-[12.5px] mt-1.5">Core · always on</small>}
          </div>
        );
      })}
    </div>
  );
}
